<template>
  <button
    class="SupporterButton"
    type="button"
    :aria-label="title"
  >
    <div
      class="SupporterButton__iconContainer"
      :style="`
        --foregroundColor: var(--color${type || 'Supporter'}Foreground);
        --backgroundColor: var(--color${type || 'Supporter'}Background);
      `"
    >
      <SupporterIcon
        class="SupporterButton__icon"
        :supporterType="type || 'Supporter'"
      />
    </div>
    <div class="SupporterButton__content">
      <h2 class="SupporterButton__title">
        {{ title }}
      </h2>
      <p class="SupporterButton__body">
        {{ body }}
      </p>
      <strong
        v-if="price"
        class="SupporterButton__price"
      >{{ formattedPrice }}</strong>
      <Icon
        v-else
        class="SupporterButton__arrowIcon"
        name="chevronRight"
      />
    </div>
  </button>
</template>

<script lang="ts" setup>
import { SupporterType } from "@shared/lib/database/enum.types";
import { computed } from "vue";
import Icon from "./Icon.vue";
import SupporterIcon from "./SupporterIcon.vue";

const {
  price,
  type,
} = defineProps<{
  type?: SupporterType,
  title: string,
  body: string,
  price?: number,
}>();

const formattedPrice = computed(() => `$${(price || 0) / 100}/mo`);
</script>

<style lang="scss" scoped>
.SupporterButton {
  --outlineColorDisabled: var(--themeDarkTransparent500);
  --backgroundColorDisabled: var(--themeLightTransparent600);
  --textColorDisabled: var(--themeDarkTransparent500);
  --buttonBackgroundColorDisabled: var(--themeColorNeutral150);
  --iconColorDisabled: var(--themeColorNeutral500);
  --transitionDuration: 350ms;

  display: grid;
  grid: auto / min-content auto;
  align-items: center;
  width: 100%;

  border: 2px solid var(--colorOffBlack);
  border-radius: 8px;
  background-color: var(--colorWhite);

  text-align: left;

  transition: var(--transitionDuration);
  transition-property: border-color, background-color;

  &:disabled {
    border-color: var(--outlineColorDisabled);
    background-color: var(--backgroundColorDisabled);
  }
}

.SupporterButton__iconContainer {
  display: grid;
  place-items: center;
  align-self: stretch;
  width: 44px;

  border-radius: 6px 0 0 6px;
  background-color: var(--backgroundColor);

  transition: background-color var(--transitionDuration);

  .SupporterButton:disabled & {
    background-color: var(--buttonBackgroundColorDisabled);
  }
}

.SupporterButton__icon {
  --iconSize: 20px;
  color: var(--foregroundColor);
  transition: color var(--transitionDuration);

  .SupporterButton:disabled & {
    color: var(--iconColorDisabled);
  }
}

.SupporterButton__content {
  position: relative;

  display: grid;
  gap: 8px;
  align-items: baseline;
  padding: calc(var(--fontSafariOffset) + 14px) 16px 16px;
}

.SupporterButton__title {
  grid-row: 1;

  font-size: 16px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);

  transition: color var(--transitionDuration);

  .SupporterButton:disabled & {
    color: var(--textColorDisabled);
  }
}

.SupporterButton__body {
  grid-column: span 2;

  color: var(--themeColorNeutral700);

  font-size: 14px;
  font-stretch: var(--fontWidthExtended);

  transition: color var(--transitionDuration);

  .SupporterButton:disabled & {
    color: var(--textColorDisabled);
  }
}

.SupporterButton__price {
  grid-row: 1;

  font-size: 16px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  text-align: right;

  .SupporterButton:disabled & {
    color: var(--textColorDisabled);
  }
}

.SupporterButton__arrowIcon {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);

  transition: color var(--transitionDuration);

  .SupporterButton:disabled & {
    color: var(--outlineColorDisabled);
  }
}
</style>
